import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./sessionCardStyles";
import { bookSession, cancelSession, getExistingZoomLink } from "./timeTableService";
import { extractLocalTime, utcToLocalDate } from "../../../utils/dateTimeUtils";
import { sessionActions } from "./sessionActions";
import { PLAN_TYPE_META } from "../../../common/constants";
import { FaRegCopy, FaCheck, FaShareAlt, FaArrowRight, FaBookOpen } from "react-icons/fa";
import watchIcon from "../../../assets/watch-icon.png";

const isMobileDevice = () => window.innerWidth <= 768;

const PLAN_ACCENT = {
  GROUP:            { color: "#6f42c1", bg: "#f3eeff" },
  ONE_ON_ONE:       { color: "#007BFF", bg: "#e8f2ff" },
  PARTNER_PRACTICE: { color: "#e91e8c", bg: "#fde8f4" },
  COMBO:            { color: "#fd7e14", bg: "#fff4e8" },
};

const PLAN_ICON = {
  GROUP:            "👥",
  ONE_ON_ONE:       "👤",
  PARTNER_PRACTICE: "🤝",
  COMBO:            "📦",
};

const getPlanTypeLabel = (planType) =>
  PLAN_TYPE_META[planType]?.displayName ?? planType;

const SessionCard = ({ session, refreshSessionStatus, highlighted }) => {
  const navigate = useNavigate();
  const [loadingAction,   setLoadingAction]   = useState(false);
  const [hovered,         setHovered]         = useState(false);
  const [openAgenda,      setOpenAgenda]       = useState(false);
  const [minutesToStart,  setMinutesToStart]   = useState(null);
  const [minutesToEnd,    setMinutesToEnd]     = useState(null);
  const [showAlert,       setShowAlert]        = useState(false);
  const [alertMessage,    setAlertMessage]     = useState("");
  const [copiedSectionId, setCopiedSectionId]  = useState(null);
  const [linkCopied,      setLinkCopied]       = useState(false);
  const [isMobile,        setIsMobile]         = useState(isMobileDevice());
  const [topicHovered,    setTopicHovered]     = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(isMobileDevice());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /* ── Time calculation ── */
  useEffect(() => {
    if (!session?.startTime || !session?.endTime) return;
    const calculate = () => {
      const now = Date.now();
      setMinutesToStart(Math.floor((new Date(session.startTime).getTime() - now) / 60000));
      setMinutesToEnd(Math.floor((new Date(session.endTime).getTime() - now) / 60000));
    };
    calculate();
    const interval = setInterval(calculate, 60000);
    return () => clearInterval(interval);
  }, [session?.startTime, session?.endTime]);

  /* ── Session state ── */
  const isCancelled     = session?.cancelled === true;
  const isBooked        = session.buttonAction === sessionActions.CANCEL || session.buttonAction === sessionActions.JOIN;
  const isExpired       = minutesToEnd !== null && minutesToEnd < 0;

  const isJoinWindow    = isBooked && ((minutesToStart <= 6 && minutesToEnd >= 0) || (minutesToStart < 0 && minutesToEnd >= 0));

  const isSessionFull   = !isBooked && session.planType !== "ONE_ON_ONE" &&
    (session.buttonAction === sessionActions.FULL || (session.remainingSlots !== undefined && session.remainingSlots <= 0));

  /* ── Button label ── */
  const getButtonLabel = () => {
    if (isCancelled)     return "CANCELLED";
    if (isExpired)       return "EXPIRED";
    if (isJoinWindow)    return "JOIN NOW";

    if (isSessionFull)   return "SESSION FULL";
    switch (session.buttonAction) {
      case sessionActions.BOOK:    return "BOOK SESSION";
      case sessionActions.CANCEL:  return "CANCEL";
      case sessionActions.UPGRADE: return "UPGRADE PLAN";
      default:                     return session.buttonAction;
    }
  };

  /* ── Button style ── */
  const getButtonStyle = () => {
    if (isCancelled || isExpired) return styles.expiredBtn;
    if (isJoinWindow)    return styles.joinBtn;

    if (isSessionFull) return styles.bookingClosedBtn;
    switch (session.buttonAction) {
      case sessionActions.BOOK:    return styles.bookBtn;
      case sessionActions.CANCEL:  return styles.cancelBtn;
      case sessionActions.UPGRADE: return styles.upgradeBtn;
      default:                     return styles.bookBtn;
    }
  };

  /* ── Action handler ── */
  const handleButtonClick = async () => {
    if (loadingAction || isCancelled || isExpired || isSessionFull) return;
    const token = localStorage.getItem("token");
    if (!token && session.buttonAction === sessionActions.BOOK) {
      window.location.href = "/register";
      return;
    }
    setLoadingAction(true);
    try {
      if (isJoinWindow) {
        const res = await getExistingZoomLink(session.id);
        console.log("[ZoomLink] session.id:", session.id, "full response:", res);
        console.log("[ZoomLink] res.data:", res.data, "type:", typeof res.data);
        if (res.data && res.data !== "Zoom link will be available soon") {
          window.open(res.data, "_blank");
        } else {
          setAlertMessage("Zoom link will be available soon");
          setShowAlert(true);
        }
      } else {
        switch (session.buttonAction) {
          case sessionActions.BOOK:    await bookSession(session.id); break;
          case sessionActions.CANCEL:  await cancelSession(session.id); break;
          case sessionActions.UPGRADE: window.location.href = "/plans"; break;
          default: break;
        }
      }
      refreshSessionStatus?.();
    } finally {
      setLoadingAction(false);
    }
  };

  /* ── Share ── */
  const handleShare = async () => {
    const url = `${window.location.origin}/timetable?session=${session.id}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const el = document.createElement("textarea");
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  /* ── Copy section ── */
  const handleCopySection = async (section) => {
    try {
      const text = section.questions?.map((q, i) => `${i + 1}. ${q.questionText}`).join("\n");
      await navigator.clipboard.writeText(text);
      setCopiedSectionId(section.id);
      setTimeout(() => setCopiedSectionId(null), 1500);
    } catch {}
  };

  const planType   = session.planType;
  const accent     = PLAN_ACCENT[planType] || { color: "#028891", bg: "#e8f7f8" };
  const planIcon   = PLAN_ICON[planType] || "📋";
  const planLabel  = getPlanTypeLabel(planType);
  const planDesc   = PLAN_TYPE_META[planType]?.description;
  const sessionDate = session.startTime
    ? new Date(session.startTime).toLocaleDateString("en-IN", { day: "numeric", month: "short", weekday: "short" })
    : null;

  /* ── Render ── */
  return (
    <div
      style={{
        ...styles.card,
        ...(hovered ? styles.cardHover : {}),
        ...(highlighted ? { outline: "3px solid #028891", outlineOffset: 2 } : {}),
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* ── Accent bar ── */}
      <div style={{ ...styles.accentBar, background: `linear-gradient(90deg, ${accent.color}, ${accent.color}99)` }} />

      {/* ══════════════════════════════════════
          ROW 1 — Plan type | Date + Time
      ══════════════════════════════════════ */}
      <div style={c.row1}>
        {/* Left: plan type badge + description */}
        <div style={c.row1Left}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <div style={{ ...c.planTypeBadge, background: accent.bg, color: accent.color, borderColor: `${accent.color}33` }}>
              <span>{planIcon}</span>
              <span style={c.planTypeLabel}>{planLabel}</span>
            </div>
            {session.remainingSlots !== undefined &&
              session.remainingSlots <= 3 &&
              session.remainingSlots > 0 &&
              !isExpired &&
              planType !== "ONE_ON_ONE" && (
                <span style={styles.slotsBadge}>
                  ⚠️ {session.remainingSlots} slot{session.remainingSlots === 1 ? "" : "s"} left
                </span>
              )}
          </div>
          {planDesc && (
            <div style={c.planDesc}>{planDesc}</div>
          )}
        </div>

        {/* Right: icon | date + time */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          <img src={watchIcon} alt="time" style={{ width: isMobile ? 72 : 88, height: isMobile ? 72 : 88, objectFit: "contain" }} />
          <div style={c.row1Right}>
            {sessionDate && <div style={c.dateSmall}>{sessionDate}</div>}
            <div style={isMobile ? styles.timeMobile : styles.time}>
              {extractLocalTime(session.startTime)}
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════
          ROW 2 — Topic
      ══════════════════════════════════════ */}
      <div style={c.row2}>
        <div style={c.topicLabel}>Topic</div>
        {session.readingPassage ? (
          <div
            onClick={() => navigate(`/study/reading/${session.readingPassage.id}`, { state: { from: "session" } })}
            onMouseEnter={() => setTopicHovered(true)}
            onMouseLeave={() => setTopicHovered(false)}
            style={{
              ...styles.readingChip,
              fontSize: isMobile ? 15 : 17,
              fontWeight: 800,
              padding: "6px 16px",
              background: topicHovered ? "#028891" : "#f0fafa",
              color: topicHovered ? "#fff" : "#00796b",
              borderColor: topicHovered ? "#028891" : "#b2dfdb",
            }}
          >
            <FaBookOpen size={isMobile ? 13 : 15} />
            {session.displayTitle || session.readingPassage.title}
          </div>
        ) : (
          <div style={isMobile ? styles.topicMobile : styles.topic}>
            {session.displayTitle || session.topic}
          </div>
        )}

{/* Status banners */}
        {isCancelled ? (
          <div style={{ ...styles.cancelledBadge, marginTop: 8 }}>❌ Session Cancelled by Host</div>
        ) : isBooked ? (
          <div style={{ ...styles.bookedBadge, marginTop: 8 }}>✅ Session Booked. See you in the meeting!</div>
        ) : null}

        {/* Upgrade reason */}
        {session.buttonAction === sessionActions.UPGRADE && (
          <div style={{ ...styles.upgradeReason, marginTop: 8 }}>
            ℹ️ {planType === "PARTNER_PRACTICE"
              ? "You need a Partner Practice plan with remaining sessions to book this session."
              : session.upgradeReason}
          </div>
        )}
      </div>

      {/* ── Divider ── */}
      <div style={styles.divider} />

      {/* ══════════════════════════════════════
          ROW 3 — Share | Speaker | Action btn
      ══════════════════════════════════════ */}
      <div style={isMobile ? c.row3Mobile : c.row3}>
        {/* Share */}
        <button
          onClick={handleShare}
          style={{ ...styles.shareBtn, ...(linkCopied ? styles.shareBtnCopied : {}) }}
        >
          {linkCopied ? <FaCheck size={11} /> : <FaShareAlt size={11} />}
          {linkCopied ? "Copied!" : "Share"}
        </button>

        {/* Speaker */}
        <div style={c.speakerChip}>
          🎤 {session.speakerName || "Host"}
        </div>

        {/* Action button */}
        <button
          style={{
            ...getButtonStyle(),
            ...(isMobile ? { width: "100%" } : { minWidth: 160 }),
          }}
          onClick={handleButtonClick}
          disabled={loadingAction || isCancelled || isExpired || isSessionFull}
        >
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", whiteSpace: "nowrap" }}>
            {loadingAction ? "Please wait..." : getButtonLabel()}
            {!loadingAction && !isCancelled && !isExpired && !isSessionFull && (
              <FaArrowRight size={11} />
            )}
          </span>
        </button>
      </div>

      {/* ── AGENDA ── */}
      {session.agendas?.length > 0 && (
        <>
          <button style={styles.agendaToggle} onClick={() => setOpenAgenda(!openAgenda)}>
            {openAgenda ? "▲ Hide discussion topics" : "▼ View discussion topics"}
          </button>

          {openAgenda && (
            <div style={styles.agendaWrap}>
              {session.agendas.map((agenda) => (
                <div key={agenda.id} style={styles.agendaCard}>
                  <div style={styles.agendaTitle}>📌 {agenda.title}</div>
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: 12,
                  }}>
                    {agenda.sections?.map((section) => (
                      <div key={section.id}>
                        <div
                          style={{
                            ...styles.sectionTitle,
                            background: copiedSectionId === section.id ? "#f0fdf4" : "transparent",
                          }}
                          onClick={() => handleCopySection(section)}
                        >
                          <span style={{ color: copiedSectionId === section.id ? "#16a34a" : "#94a3b8" }}>
                            {copiedSectionId === section.id ? <FaCheck size={12} /> : <FaRegCopy size={12} />}
                          </span>
                          {section.section}
                        </div>
                        {section.questions?.map((q) => (
                          <div key={q.id} style={styles.question}>• {q.questionText}</div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── ALERT ── */}
      {showAlert && (
        <div style={styles.customAlertOverlay}>
          <div style={styles.customAlertBox}>
            <div style={styles.customAlertText}>{alertMessage}</div>
            <button style={styles.customAlertButton} onClick={() => setShowAlert(false)}>OK</button>
          </div>
        </div>
      )}

      {/* ── ZOOM HINT ── */}
      {!isCancelled && !isExpired && (
        <div style={c.zoomHint}>📲 Please install the Zoom application to join the meeting.</div>
      )}
    </div>
  );
};

/* ── Card-specific layout styles ── */
const c = {
  /* Row 1 */
  row1: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "16px 20px 10px",
    gap: 12,
  },
  row1Left: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    flex: 1,
  },
  planTypeBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 12px",
    borderRadius: 999,
    border: "1px solid",
    fontSize: 13,
    fontWeight: 700,
    width: "fit-content",
  },
  planTypeLabel: {
    fontSize: 13,
    fontWeight: 700,
  },
  planDesc: {
    fontSize: 11,
    color: "#94a3b8",
    lineHeight: 1.4,
    maxWidth: 200,
  },
  row1Right: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 2,
    flexShrink: 0,
  },
  dateSmall: {
    fontSize: 15,
    color: "#94a3b8",
    fontWeight: 600,
    letterSpacing: "0.02em",
  },

  /* Row 2 */
  row2: {
    padding: "4px 20px 10px",
  },
  topicLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    marginBottom: 4,
  },
  zoomHint: {
    fontSize: 12,
    color: "#0369a1",
    background: "#f0f9ff",
    borderTop: "1px solid #bae6fd",
    padding: "8px 20px",
    fontWeight: 500,
    textAlign: "center",
  },

  /* Row 3 */
  row3: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    padding: "0 20px 18px",
  },
  row3Mobile: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    padding: "0 16px 16px",
  },
  speakerChip: {
    fontSize: 13,
    fontWeight: 700,
    color: "#64748b",
    display: "flex",
    alignItems: "center",
    gap: 6,
    background: "rgba(2,136,145,0.07)",
    padding: "6px 12px",
    borderRadius: 999,
    border: "1px solid rgba(2,136,145,0.12)",
    whiteSpace: "nowrap",
  },
};

export default SessionCard;
